/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { AppConfig } from '@/types/config';
import { useAuth } from '@/providers/auth';
import { useBasket } from '@/providers/basket';
import { useOptionalDeliveryZip } from '@/providers/store-and-delivery-zip-provider';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';
import { pushSessionStart, pushZipMapFields } from '@/lib/gtm/events/page';
import { pushGlobalSessionData } from '@/lib/gtm/events/global-session';
import type { GtmUserHashesActionResponse } from '@/routes/action.gtm-user-hashes';
import type { GtmUserContextActionResponse } from '@/routes/action.gtm-user-context';

/**
 * GtmProvider initializes GTM session tracking and pushes global session data.
 *
 * Renders nothing — this is a side-effect-only component that:
 * 1. Fires `initial_page` once per browser session (via sessionStorage)
 * 2. Pushes global session data (basket, auth, locale, zip, hasOrders, cartId, cartType) on state changes
 * 3. Pushes Ashley custom `zipMapFields` block once per zip resolution
 *
 * Place inside the provider tree so it has access to Auth, Basket, Site, and Config contexts.
 */
export function GtmProvider() {
    const config = useConfig<AppConfig>();
    const auth = useAuth();
    const basket = useBasket();
    const deliveryZip = useOptionalDeliveryZip();
    const location = useLocation();
    const { trackingConsent } = useTrackingConsent();
    const { currency, locale } = useSite();

    const isEnabled = config.engagement?.gtm?.enabled ?? false;
    const hasConsent = trackingConsent === TrackingConsent.Accepted;
    const canTrack = isEnabled && hasConsent;
    // Undefined defaults to true — see `AppConfig.engagement.gtm.emitLegacyAliases`.
    const emitLegacyAliases = config.engagement?.gtm?.emitLegacyAliases ?? true;

    const sessionStartedRef = useRef(false);

    const localeId = locale?.id ?? config.i18n?.fallbackLng ?? 'en-US';
    const currencyCode = basket?.currency ?? currency ?? 'USD';

    // Session start: fire once per session when tracking is first allowed
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (sessionStartedRef.current || !canTrack) return;

        const isMember = auth?.userType === 'registered';

        const fired = pushSessionStart({ isMember, currencyCode, locale: localeId });
        if (fired) {
            sessionStartedRef.current = true;
        }
    }, [canTrack, auth?.userType, currencyCode, localeId]);

    // Server-side email hash (GA4 user-scope `emailsha256` — INT-21 Phase G).
    //
    // `PublicSessionData` is intentionally minimal and controlled by the runtime, so the
    // hash cannot be piggy-backed onto the root loader auth payload. Instead, once
    // tracking is allowed for a registered shopper, we fetch the SCAPI-serialized
    // customer response through `/action/gtm-user-hashes`, which reads
    // `c_hashedEmailSha256` off `Profile.custom.hashedEmailSha256` (stamped by the
    // `sfnext_ashley_hooks` `dw.shop.customer.afterPOST` / `modifyGETResponse` hooks).
    // Guest sessions and failed lookups yield `undefined`, and the push simply omits
    // `emailsha256` — the field is legacy GA3 audience parity, never a hard blocker.
    const [emailSha256, setEmailSha256] = useState<string | undefined>(undefined);
    const hashFetchedForCustomerRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!canTrack) return;
        if (auth?.userType !== 'registered' || !auth.customerId) {
            // Clear any stale hash from a previous registered session (e.g. logout).
            if (emailSha256 !== undefined) setEmailSha256(undefined);
            hashFetchedForCustomerRef.current = undefined;
            return;
        }
        if (hashFetchedForCustomerRef.current === auth.customerId) return;
        hashFetchedForCustomerRef.current = auth.customerId;

        void fetch('/action/gtm-user-hashes', {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (res) => {
                if (!res.ok) return;
                const data = (await res.json()) as GtmUserHashesActionResponse;
                if (data.enabled && data.hashedEmailSha256) {
                    setEmailSha256(data.hashedEmailSha256);
                }
            })
            .catch(() => {
                // Fail-open: the emailsha256 push is best-effort. The route already
                // logs the SCAPI failure server-side, so nothing more to do here.
            });
    }, [canTrack, auth?.userType, auth?.customerId, emailSha256]);

    // Server-side registered-shopper context (INT-21 Phase F.2) — legacy
    // `dimension21` / `user_properties.is_returning_customer`. Same pattern
    // as the hash fetch above: one SCAPI round-trip per customer session,
    // cached in a ref so we don't re-fetch across route navigations.
    const [isReturningCustomer, setIsReturningCustomer] = useState<boolean | undefined>(undefined);
    const contextFetchedForCustomerRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!canTrack) return;
        if (auth?.userType !== 'registered' || !auth.customerId) {
            // Clear on logout so the next registered session picks up fresh state.
            if (isReturningCustomer !== undefined) setIsReturningCustomer(undefined);
            contextFetchedForCustomerRef.current = undefined;
            return;
        }
        if (contextFetchedForCustomerRef.current === auth.customerId) return;
        contextFetchedForCustomerRef.current = auth.customerId;

        void fetch('/action/gtm-user-context', {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (res) => {
                if (!res.ok) return;
                const data = (await res.json()) as GtmUserContextActionResponse;
                if (data.enabled) {
                    setIsReturningCustomer(data.isReturningCustomer);
                }
            })
            .catch(() => {
                // Fail-open: dimension21 is best-effort. Same posture as the
                // hash fetch — the route logs SCAPI failure server-side.
            });
    }, [canTrack, auth?.userType, auth?.customerId, isReturningCustomer]);

    // Global session data: push on basket/auth/zip/returning changes
    const previousSnapshotRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!canTrack) return;

        const zip = deliveryZip?.zip;
        const basketId = basket?.basketId;
        const authKey = `${auth?.userType ?? 'guest'}|${auth?.customerId ?? ''}|${emailSha256 ?? ''}`;
        const returningKey = typeof isReturningCustomer === 'boolean' ? String(isReturningCustomer) : '';
        const snapshot = `${basketId ?? ''}|${authKey}|${zip ?? ''}|${returningKey}`;

        if (snapshot === previousSnapshotRef.current) {
            return;
        }
        previousSnapshotRef.current = snapshot;

        const isRegistered = auth?.userType === 'registered';

        pushGlobalSessionData(
            {
                loggedInState: isRegistered ? 'logged-in' : 'guest',
                basket,
                locale: localeId,
                customerId: isRegistered ? auth?.customerId : undefined,
                // Server-computed hash sourced from `/action/gtm-user-hashes` (INT-21 Phase G).
                // Only set for registered shoppers; guests omit this field.
                emailsha256: isRegistered ? emailSha256 : undefined,
                // INT-21 Phase F — session ZIP + is-returning-customer.
                zip,
                isReturningCustomer: isRegistered ? isReturningCustomer : undefined,
            },
            { emitLegacyAliases }
        );
    }, [
        canTrack,
        basket,
        auth?.userType,
        auth?.customerId,
        localeId,
        emailSha256,
        emitLegacyAliases,
        deliveryZip?.zip,
        isReturningCustomer,
    ]);

    // Ashley custom `zipMapFields` push (INT-21 Phase F.4).
    //
    // Mirrors legacy `zipMapFieldsDataLayer.isml` which fires once per page
    // render server-side. On storefront-next we push once per distinct
    // (zip, priceZone, path) tuple:
    //   - We ref-guard on the tuple identity so back-to-back client-side
    //     navigations to the same page don't re-push identical fields.
    //   - `pathname` participates in the key so a fresh page render (even
    //     if zip/priceZone are unchanged) re-pushes — matching legacy's
    //     per-page cadence.
    //
    // ADAPTER GAP — RCID / fulfillerName / marketZone / stateCode still owed
    // by a personalization adapter (INT-58 territory: the legacy SFCC
    // `Personalization.setCustomerPersonalization` writes these to
    // `session.custom.rdcId` / `session.custom.fulfillerName` /
    // `session.custom.marketZone` / `session.custom.stateCode`, and the
    // storefront-next `useDeliveryZip` does not yet surface them). Until
    // the adapter is extended, only `priceZone` populates; the rest are
    // omitted rather than fabricated. See `docs/INT-21-GA-STATUS.md` §2.3.
    const previousZipMapFieldsKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!canTrack) return;

        const zip = deliveryZip?.zip;
        const priceZone = deliveryZip?.priceZone ?? undefined;
        // Without a zip we have nothing meaningful to push — skip entirely.
        if (!zip) return;

        const key = `${zip}|${priceZone ?? ''}|${location.pathname}`;
        if (key === previousZipMapFieldsKeyRef.current) return;
        previousZipMapFieldsKeyRef.current = key;

        pushZipMapFields({
            priceZone: priceZone ?? undefined,
            // rcid / fulfillerName / marketZone / stateCode: unpopulated until
            // the personalization adapter surfaces them client-side.
        });
    }, [canTrack, deliveryZip?.zip, deliveryZip?.priceZone, location.pathname]);

    return null;
}
